/**
 * @module Model
 */

import { ORDER_VENDOR_SUFFIX, OrderTemplate } from '@feature/orders/shared';
import { AgentRequestStatus, Individual } from '@feature/users/shared';
import { CustomFieldValue } from '@app/components/custom-fields/shared/custom-field.model';
import { Address, EmailCC } from '@feature/profiles/shared';
import { CompanyPair } from '@feature/companies/shared';

/**
 * Class representing a reference to a user agent in the application.
 *
 * This class encapsulates various properties related to a user agent.
 * It is used to manage and represent user agent data within the application.
 *
 * @class
 * @model
 */
export class UserAgentRef {
	/**
	 * The unique identifier for the client associated with an agent.
	 * @type {number}
	 */
	id: number;

	/**
	 * The identifier for the agent associated with this reference.
	 * @type {number}
	 */
	agentId: number;

	/**
	 * The full name of the agent.
	 * @type {string}
	 */
	agentFullName: string;

	/**
	 * The email address of the agent.
	 * @type {string}
	 */
	agentEmail: string;

	/**
	 * The identifier for the agency associated with the agent.
	 * @type {number}
	 */
	agencyId: number;

	/**
	 * The name of the agency.
	 * @type {string}
	 */
	agencyName: string;

	/**
	 * The identifier for the client associated with the agent.
	 * @type {number}
	 */
	clientId: number;

	/**
	 * The full name of the client.
	 * @type {string}
	 */
	clientFullName: string;

	/**
	 * The first name of the client.
	 * @type {string}
	 */
	clientFirstName: string;

	/**
	 * The last name of the client.
	 * @type {string}
	 */
	clientLastName: string;

	/**
	 * The email address of the client.
	 * @type {string}
	 */
	clientEmail: string;

	/**
	 * The default order template for the client.
	 * @type {OrderTemplate}
	 */
	clientDefaultTemplate: OrderTemplate;

	/**
	 * A unique key for the request associated with the agent.
	 * @type {string}
	 */
	requestKey: string;

	/**
	 * The current status of the agent request.
	 * @type {AgentRequestStatus}
	 */
	status: AgentRequestStatus;

	/**
	 * The date when the user agent reference was last updated.
	 * @type {Date}
	 */
	updatedDate: Date;

	/**
	 * Optional array of custom field values associated with the reference.
	 * @type {Array<CustomFieldValue> | undefined}
	 */
	customFieldValues?: Array<CustomFieldValue>;
}

/**
 * Class representing a shortened version of the user.
 *
 * This class encapsulates various properties related to a user, but in a shortened format.
 *
 * @class
 * @model
 */
export class UserPair {
	/**
	 * The unique identifier for the user.
	 * @type {number}
	 */
	id: number;

	/**
	 * The first name of the user.
	 * @type {string}
	 */
	firstName: string;

	/**
	 * The middle name of the user (optional).
	 * @type {string}
	 */
	middleName?: string;

	/**
	 * The last name of the user.
	 * @type {string}
	 */
	lastName: string;

	/**
	 * The full name of the user.
	 * @type {string}
	 */
	fullName: string;

	/**
	 * The email address of the user.
	 * @type {string}
	 */
	email: string;

	/**
	 * The time zone of the user, if applicable.
	 * @type {string | undefined}
	 */
	timeZone?: string;

	/**
	 * Indicates whether the user is new.
	 * @type {boolean}
	 */
	isNew: boolean;

	/**
	 * Indicates whether the user is banned.
	 * @type {boolean}
	 */
	isBanned: boolean;

	/**
	 * Indicates whether the user has migrated.
	 * @type {boolean}
	 */
	isMigrated: boolean;

	/**
	 * Indicates whether the user is an individual.
	 * @type {boolean}
	 */
	isIndividual: boolean;

	/**
	 * Indicates whether the user is a company.
	 * @type {boolean}
	 */
	isCompany: boolean;

	/**
	 * Indicates whether the user is a member.
	 * @type {boolean}
	 */
	isMember: boolean;

	/**
	 * Indicates whether the user is an agent.
	 * @type {boolean}
	 */
	isAgent: boolean;

	/**
	 * Indicates whether the user is a talent.
	 * @type {boolean}
	 */
	isTalent: boolean;

	/**
	 * Indicates whether the user is a customer.
	 * @type {boolean}
	 */
	isCustomer: boolean;

	/**
	 * Indicates whether the user is an admin.
	 * @type {boolean}
	 */
	isAdmin: boolean;

	/**
	 * Indicates whether the user can be an agent.
	 * @type {boolean}
	 */
	canBeAgent: boolean;

	/**
	 * Indicates whether the user can be a customer.
	 * @type {boolean}
	 */
	canBeCustomer: boolean;

	/**
	 * Indicates whether the user allows direct payments AND through an agent.
	 * @type {boolean}
	 */
	canBePaidForPerformanceAsBoth?: boolean;

	/**
	 * Indicates whether the user allows non-performance payment display option.
	 * @type {boolean}
	 */
	canBePaidAsVendor?: boolean;

	/**
	 * Indicates whether the user allows the access to non-performance payment.
	 * @type {boolean}
	 */
	canPayOrRequestAsVendor?: boolean;

	/**
	 * Indicates the preference that controls the display options for performance payment request.
	 * @type {boolean}
	 */
	canBeRequestedForPerformance?: boolean;

	/**
	 * The current signup step for the user.
	 *
	 * This property is used to track the user's progress in the signup process.
	 *
	 * @type {string}
	 * @note Has to be pulled separately for individual.
	 */
	signupStep: string;

	/**
	 * The count of orders associated with the payee user.
	 * @type {number}
	 */
	payeeOrdersCount: number;

	/**
	 * Array of order templates associated with the user (optional).
	 * @type {Array<OrderTemplate> | undefined}
	 */
	orderTemplates?: Array<OrderTemplate>;

	/**
	 * Default order template for the user (optional).
	 * @type {OrderTemplate | undefined}
	 */
	defaultTemplate?: OrderTemplate;

	/**
	 * The address property compatible with AnyUser type.
	 * @type {never}
	 */
	address?: never;

	/**
	 * The individual user type property compatible with AnyUser type.
	 * @type {Individual | undefined}
	 */
	individual?: Individual;

	/**
	 * An array of email CCs compatible with AnyUser type.
	 * @type {Array<EmailCC> | undefined}
	 */
	emailCCs?: Array<EmailCC>;

	/**
	 * The billing address associated with the user, to be used in AnyUser.
	 * @type {any | undefined}
	 */
	billAddress?: any;

	/**
	 * Company details associated with the user (optional).
	 * @type {any | undefined}
	 */
	company?: any;

	/**
	 * Custom field values that are mostly required for the payment request.
	 * @type {Array<CustomFieldValue>}
	 */
	customFieldValues?: Array<CustomFieldValue>;
}

/**
 * Class representing a user associated with an order.
 *
 * This class encapsulates the properties related to an order user.
 * It manages user information specifically in the context of order processing.
 *
 * @class
 * @model
 */
export class OrderUser {
	/**
	 * The first name of the order user (optional).
	 * @type {string}
	 */
	firstName?: string;

	/**
	 * The middle name of the order user (optional).
	 * @type {string}
	 */
	middleName?: string;

	/**
	 * The last name of the order user.
	 * @type {string}
	 */
	lastName?: string;

	/**
	 * The full name of the order user.
	 * @type {string}
	 */
	fullName: string;

	/**
	 * The email address of the order user.
	 * @type {string}
	 */
	email: string;

	/**
	 * The billing address of the order user.
	 * @type {string}
	 */
	billAddress: string;

	/**
	 * The billing address of the order user.
	 * @type {string}
	 */
	shipAddress: string;

	/**
	 * The company associated with the order user.
	 * @type {string}
	 */
	company: string;

	/**
	 * The address property compatible with AnyUser type.
	 * @type {never}
	 */
	address?: never;

	/**
	 * The phone property compatible with AnyUser type.
	 * @type {never}
	 */
	phone?: never;

	/**
	 * An array of email CCs compatible with AnyUser type.
	 * @type {never}
	 */
	emailCCs?: never;

	/**
	 * The individual user type property compatible with AnyUser type.
	 * @type {never}
	 */
	individual?: never;
}

/**
 * Class representing a shortened version of order recipient, which can be many instances.
 *
 * A recipient is an individual, a company, or a non-performance vendor
 * that receives money from an invoicee. A recipient can be a performer,
 * in case of a publisher creating a payment, or a publisher, in case of a performer invoicing them.
 * An agent is a member of an agency that represents the recipient in the payment process.
 *
 * @class
 * @model
 */
export class OrderUserPair {
	/**
	 * The user pair representing the invoicee.
	 * @type {UserPair}
	 */
	recipient: UserPair;

	/**
	 * User pair representing the invoicer agent (optional).
	 * @type {UserPair}
	 */
	agent?: UserPair;

	/**
	 * Company pair representing the client's agency (optional).
	 * @type {CompanyPair}
	 */
	agency?: CompanyPair;

	/**
	 * Company pair representing the invoicee (optional).
	 *
	 * @note When a publisher creates a payment, the recipient is another publisher-vendor.
	 *
	 * @type {CompanyPair}
	 */
	recipientCompany?: CompanyPair;

	/**
	 * Indicates whether the recipient is a vendor.
	 * @type {boolean} [false]
	 */
	isVendor?: boolean = false;

	/**
	 * Gets the unique identifier for the payee-agent pair.
	 *
	 * This getter constructs an ID string based on the payee's ID and the agent's ID (if available).
	 *
	 * @getter
	 * @returns {string} The unique identifier for the payee-agent pair.
	 */
	get id(): string {
		return this.recipient?.id?.toString() + (this.agent ? '-' + this.agent?.id : '');
	}

	/**
	 * Gets the display label for the recipient's pair.
	 *
	 * This getter returns a formatted string that indicates whether the payee is a vendor or not,
	 * along with the appropriate names based on the type of payee (individual, company, etc.).
	 *
	 * @getter
	 * @returns {string} The display text for the payee-agent pair.
	 */
	get text(): string {
		if (this.isVendor) {
			// @note Vendor can be an international payer, an individual payer/performer or a company payer.
			// International and individual payers don't have an LLC name. Use the user's name instead.
			// Companies don't have an LLC name but must provide a company name. Use it instead.
			return (this.recipientCompany?.name?.length > 0 ? this.recipientCompany?.name : this.recipient?.fullName) + ` (${ORDER_VENDOR_SUFFIX})`;
		} else if (this.recipient?.isCustomer) {
			const recipientName: string = (this.recipientCompany?.name || this.recipient?.fullName);
			const recipientAddress: string = (this.recipientCompany?.name?.length > 0
				? (this.recipientCompany?.address as any)?.countryName
				: (this.recipient?.address as any)?.countryName);
			return `${recipientName} (${recipientAddress})`;
		} else if (this.recipientCompany?.name?.length > 0) {
			return `${this.recipientCompany?.name} (${(this.recipientCompany?.address as any)?.countryName})`;
		}

		const receiverType = this.agency ? this.agency?.name : 'Direct pay';
		return this.recipient?.fullName + ` (${receiverType} - ${this.recipient?.defaultTemplate?.title})`;
	}

	/**
	 * Gets the address associated with the recipient's pair.
	 *
	 * This getter determines the appropriate address to return based on the presence of the recipient's
	 * company or agency representation. If neither is available, it defaults to the payee's address.
	 *
	 * @getter
	 * @returns {Address} The address associated with the recipient's pair.
	 */
	get address(): Address {
		if (this.recipientCompany != null) return this.recipientCompany?.address;
		return this.agency ? this.agency?.address : this.recipient?.address;
	}

	/**
	 * Gets the default order template for the payee.
	 *
	 * This getter ensures that the actual payee's order template is used, rather than the agent's.
	 *
	 * @getter
	 * @returns {OrderTemplate} The default order template for the payee.
	 */
	get defaultTemplate(): OrderTemplate {
		return this.recipient?.defaultTemplate;
	}

	/**
	 * Gets the custom field values depending on the profile.
	 *
	 * @returns {Array<CustomFieldValue>}
	 */
	get customFieldValues(): Array<CustomFieldValue> {
		return this.recipientCompany?.customFieldValues != null
			? this.recipientCompany?.customFieldValues
			: this.recipient?.customFieldValues;
	}
}
